import { useEffect, useState } from "react";
import { FileText, Download, ShieldCheck, Loader2, RefreshCw } from "lucide-react";
import { getReports, downloadReport } from "../lib/api";
import { useT, useLang, timeAgo } from "../lib/i18n";

const SEV = {
  critical: { text: "text-red-400", bg: "bg-red-950/20", ring: "border-l-red-500", key: "sev_critical" },
  warning: { text: "text-amber-400", bg: "bg-amber-950/20", ring: "border-l-amber-500", key: "sev_warning" },
  info: { text: "text-emerald-400", bg: "bg-emerald-950/20", ring: "border-l-emerald-500", key: "sev_info" },
};

export const ReportsArchive = () => {
  const t = useT();
  const lang = useLang();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    getReports()
      .then((d) => setReports(d.reports || []))
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    const poll = setInterval(load, 30000);
    return () => clearInterval(poll);
  }, []);

  return (
    <div className="flex-1 min-h-0 bg-[#06090E] overflow-y-auto resilio-scroll" data-testid="reports-archive">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="flex items-center gap-2.5 text-xl font-mono font-bold tracking-tight text-slate-100">
              <FileText className="h-5 w-5 text-cyan-400" /> {t.reports_title}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{t.reports_subtitle}</p>
          </div>
          <button
            onClick={load}
            data-testid="reports-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#111726] border border-slate-700 text-[11px] font-mono text-slate-300 hover:border-cyan-500 hover:text-cyan-300 transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" /> {lang === "en" ? "Refresh" : "Actualizar"}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 font-mono text-sm py-16 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> …
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center text-slate-600 font-mono text-sm py-16 border border-dashed border-slate-800 rounded-lg">
            {t.no_reports}
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="reports-list">
            {reports.map((r) => {
              const s = SEV[r.severity] || SEV.info;
              const title = lang === "en" && r.titulo_en ? r.titulo_en : r.titulo;
              return (
                <div
                  key={r.reference}
                  data-testid="report-row"
                  className={`border border-slate-800 border-l-4 ${s.ring} ${s.bg} rounded-md p-4 flex items-center justify-between gap-4`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[11px] text-cyan-400 tracking-wider">{r.reference}</span>
                      <span className={`text-[10px] font-mono font-semibold ${s.text}`}>{t[s.key]}</span>
                      {r.sovereign && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-fuchsia-500/15 border border-fuchsia-500/40 text-[9px] font-mono text-fuchsia-300">
                          <ShieldCheck className="h-3 w-3" /> {lang === "en" ? "SOVEREIGN" : "SOBERANA"}
                        </span>
                      )}
                    </div>
                    <div className="text-[13px] text-slate-100 truncate">{title}</div>
                    <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500 mt-1">
                      <span className="text-slate-400">{r.municipio}</span>
                      <span>·</span>
                      <span>{r.ccaa}</span>
                      <span>·</span>
                      <span>{t.generated_label} {timeAgo(r.created_at, lang)}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadReport(r.reference, lang)}
                    data-testid="report-download-link"
                    className="shrink-0 inline-flex items-center gap-1.5 rounded border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-[11px] font-mono tracking-wider text-cyan-300 hover:bg-cyan-500/20 transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" /> PDF
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
